/** @module SiteAdmin/HomePage */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/dom-class",
	"dojo/store/Memory",
	"dojo/store/Observable",
	"dojo/topic",
	"dojo/when",
	"dijit/TitlePane",
	"dgrid/OnDemandGrid",
	"dgrid/Selection",
	"dgrid/extensions/DijitRegistry",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/_base/modules",
	"csi/action",
	"csi/comm",
	"csi/config/preferences",
	"csi/def",
	"csi/dojox/layout/GridContainerLite",
	"csi/html",
	"csi/html/Widget",
	"csi/locale",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state",
	"csi/state/rootStore",
	"csi/xml",
	"./HomePage/Pane",
	"./HomePage/Table",
	"./HomePage/util",
	"dojo/text!SiteAdmin/templates/homepage.html",

	"dijit/form/ToggleButton",
	"dijit/layout/ContentPane"
], function(declare, lang, dom, domClass, Memory, Observable, topic, when, TitlePane, OnDemandGrid, Selection, GridDijitRegistry,
	csi, Deferred, _, modules, csiAction, comm, configPreferences, def, GridContainerLite, html, Widget, locale, reflection, sessionConstants, state, {routerStore, sessionStore}, xml, HomePagePane, HomePageTable, HomePageUtil, template) {

	/**
	 * @typedef {Object} LayoutPane - Description of Pane included in LayoutDefinition.
	 *  @property {number} column - Column that includes pane.
	 *  @property {number} columnPosition - Index indicating where to insert pane in column.
	 *  @property {string} [contextState] - enum filter value for sourcing views
	 *  @property {string} contextType - Class type owning view, such as Season or MaterialSecurityGroup.
	 *  @property {string} contextName - Name of the class instance, such as name of the Category1, Category2, Collection.
	 *  @property {string} contextUrl - Context URL whose contents are being examined.
	 *  @property {string} id - Unique id for pane that is used to help identify selected custom view for pane.
	 *  @property {boolean} open - Indicates if title pane is open or closed.
	 *  @property {string} title - Title of pane.
	 *  @property {string} view - Id of View used in pane.
	 */

	/**
	 * @typedef {Object} LayoutDefinition - structure saved as JSON to UserPreferences.HomePageLayout attribute.
	 *  @property {number} layout - Choice of layout which will determine the number of columns and widths of those columns.
	 *  @property {LayoutPane[]} panes - List of panes included in the layout.
	 */

	var ACTION_CONFIGURE = 'DashboardConfigure';
	var ACTION_COPY = 'DashboardCopy';
	var ACTION_LAYOUT = 'DashboardChangeLayout';
	var ACTION_NEW = 'DashboardNew';
	var ACTION_REFRESH = 'ViewRefresh';
	var ATTR_LAYOUT = 'HomePageLayout';
	var CSS_ROOT = 'csi-homepage';
	var CSS_CONFIGURE = 'csi-homepage-configure';
	var CSS_EXPANDED = 'csi-homepage-expanded';
	var MINIMUM_VIEW_WIDTH = 500;

	var DISABLE_DELETE_WIDGETS = {
		Announcements: 1
	};
	var VIEW_CLASSES = {
		"Announcements": "CompanyInfo",
		"MyActions": "WorkflowViews",
		"MyActivities": "CalendarDescriptor"
	};

	var ICONS = {
		"Announcements": "fas fa-bullhorn",
		"MyActions": "csi-svg-icon-size40 csi-homepage-new-actions",
		"MyActivities": "fas fa-list-alt"
	};

	// TODO - eventually we may offer more layouts, but for now we offer just 1
	var LAYOUTS = [
		{columns: 2}
	];

	const STRUCTURE_ITEM_CLASSES = [
		"Season",
		"Category1",
		"Category2",
		"Collection"
	];

	const STRUCTURE_ITEM = "StructureItem";


	/**
	 * Return layout definition for home page, which could be null if first time visited
	 * @return {LayoutDefinition|null}
	 */
	function _getLayoutDefinition() {
		const layoutString = sessionStore.userPreferences[ATTR_LAYOUT];
		return layoutString ? JSON.parse(layoutString) : null;
	}

	/**
	 * Return store used for grid displaying widgets available to add to home page.
	 * @param {View} defView - view definition for home page
	 * @return {Memory}
	 */
	function _getNewStore(defView) {
		var items = csi.transform(defView.m_defs, function(viewRef) {
			var id = viewRef.m_id;
			var type = VIEW_CLASSES[id];
			var rc = reflection.getClass(type);

			if (def.getClassViewDef(rc, id, true, true)) {
				return {
					id: id,
					name: locale.typeView(type, id),
					css: ICONS[id]
				};
			}

			return null;
		}) || [];

		if (!sessionConstants.isLocalUser) {
			if (reflection.getClass("Project")?.enabled) {
				items.push({id: "Project", name: locale.resource("ProjectMgmt"), css: "csi-svg-icon-size40 csi-homepage-new-projectmanagement"});
			}
			items.push({id: "Style", name: locale.resource("StyleManagement"), css: "csi-svg-icon-size40 csi-homepage-new-style"});
			items.push({id: "Material", name: locale.resource("MaterialManagement"), css: "csi-svg-icon-size40 csi-homepage-new-material"});
		}

		items.push({id: "Sourcing", name: locale.resource("Sourcing"), css: "csi-svg-icon-size40 csi-homepage-new-sourcing"});

		if (!sessionConstants.isLocalUser && modules.hasModule("FormulatedProduct") && (reflection.getClass("Food")?.enabled || reflection.getClass("FormulatedMaterial")?.enabled)) {
			items.push({id: "FormulatedProduct", name: locale.resource("FormulatedProducts"), css: "csi-svg-icon-size40 csi-homepage-new-formulatedproduct"});
		}

		if (reflection.getClass("ProofTask")?.enabled) {
			items.push({id: "Proofing", name: locale.resource("ProofingMgmt"), css: "csi-svg-icon-size40 csi-homepage-new-proofing"});
		}

		return Observable(new Memory({data: items}));
	}

	/**
	 * Return parameters to describe pane in layout.
	 * @param {HomePagePane} titlePane
	 * @return {LayoutPane}
	 */
	function _getPaneLayout(titlePane) {
		var view = titlePane.getView && titlePane.getView();

		// ignore pane to add new widgets
		if (!view) {
			return null;
		}

		var state = view.get("dashboardState");
		var url = view.getViewURL();
		var contextType = view.dashboardName ? view.dashboardType : view.getViewNodeType();

		var params = {
			column: titlePane.get('column'),
			contextType,
			contextName: view.dashboardName,
			id: titlePane.get('homePageId'),
			open: titlePane.get("open"),
			title: titlePane.get('title'),
			view: view.defView.getId()
		};

		if (state) {
			params.contextState = state;
		}

		if (url != view.viewParent.getViewURL()) {
			params.contextUrl = url;
		}

		return params;
	}

	/**
	 * Return title pane's position in column.
	 * @param {TitlePane} titlePane
	 * @return {number}
	 */
	function _getPanePosition(titlePane) {
		return html.getElementIndex(titlePane.domNode);
	}

	/**
	 * Write operation to save layout to UserPreferences.HomePageLayout.
	 * @param {Writer} writer
	 * @param {LayoutDefinition} layout
	 */
	function _writeLayout(writer, layout) {
		writer.changeNode(sessionStore.userPreferencesUrl).changeAttribute(ATTR_LAYOUT, xml.ATTR_STRING, JSON.stringify(layout));
	}

	/**
	* Validate delete action on widget is available or not
	* @param {Object} defView - the view object
	* @return {boolean} - true if widget can delete from home page
	*/
	function _isEnabledDelete(defView) {
		return state.get("admin") || !DISABLE_DELETE_WIDGETS[defView.m_id];
	}

	return declare(Widget, {
		templateString: template,

		/**
		 * @property {DOMNode} expandContainerNode - DOM node containing expanded grid container
		 */
		expandContainerNode: null,

		/**
		 * @property {DOMNode} gridContainerNode - DOM node containing grid container
		 */
		gridContainerNode: null,

		/**
		 * @property {string} welcomeMessage - welcome message for user
		 */
		welcomeMessage: null,

		/**
		 * @property {string} _bConfigure - true when in configuration mode
		 */
		_bConfigure: false,

		/**
		 * @property {number} _expandedColumn - column containing expanded pane
		 */
		_expandedColumn: null,

		/**
		 * @property {GridContainerLite} _expandContainer - grid container for single expanded pane
		 */
		_expandContainer: null,

		/**
		 * @property {number} _expandedPosition - position of expanded pane within column
		 */
		_expandedPosition: null,

		/**
		 * @property {GridContainerLite} _gridContainer - grid container for all home page panes
		 */
		_gridContainer: null,

		/**
		 * @property {number} _minimumWidth - width at which grid switched to one column
		 */
		_minimumWidth: null,

		/**
		 * @property {HomePagePane} _newPane - title pane show in configuration mode and used to add new panes to home page
		 */
		_newPane: null,

		/**
		 * @property {Memory} _newStore - observable memory store of items available for adding to the view
		 */
		_newStore: null,

		postMixInProperties: function() {
			const self = this;

			this.inherited(arguments);
			this.welcomeMessage = this.welcomeMessage || locale.resource('WelcomeUser', [sessionStore.currentUser.$Name]);

			if (sessionConstants.isLocalUser) {
				VIEW_CLASSES["SupplierMyActions"] = VIEW_CLASSES["MyActions"];
				ICONS["SupplierMyActions"] = ICONS["MyActions"];
			}

			// make sure content when dragging matches content when stationary
			this.own(
				topic.subscribe("/dojox/mdnd/drag/start", function(node, sourceArea, dropIndex) {
					if (dom.isDescendant(sourceArea.node, self.domNode)) {
						var a = [CSS_ROOT];

						if (self._bConfigure) {
							a.push(CSS_CONFIGURE);
						}

						if (self._expandContainer) {
							a.push(CSS_EXPANDED);
						}

						domClass.add(node, a.join(' '));
					}
				}),
				topic.subscribe("/dojox/mdnd/drop", function(node, sourceArea, dropIndex) {
					if (dom.isDescendant(sourceArea.node, self.domNode)) {
						domClass.remove(node, CSS_ROOT);
						domClass.remove(node, CSS_CONFIGURE);
						domClass.remove(node, CSS_EXPANDED);
					}
				})
			);
		},

		/**
		 * Disable fading by returning null
		 */
		getFadeNode: function() {
			return null;
		},

		/**
		 * Return ViewRef from HomePage view based on pane parameters
		 * @param {LayoutPane} pane
		 * @return {ViewRef}
		 */
		getHomeViewRef: function(pane) {
			var id = pane.view;
			var rc = reflection.getClass(pane.contextType);
			var vr;

			if (id && !rc) {
				// if only id is specified, then just find by key
				vr = this.defView.defsByKey[id];
			} else {
				// find by id and target
				vr = _.findWhere(this.defView.m_defs, {m_id: id, rcTarget: rc});
			}

			return vr;
		},

		/**
		 * Return Memory store holding available views for all given target types. The store items will indicate to which types they apply.
		 * @param {string[]} [targetIds] - optional list of view ids to include in store
		 * @param {string[]} [targetTypes] - optional list of view target types to include in store
		 * @return {Memory}
		 */
		getHomeViewRefStore: function(targetIds, targetTypes) {
			const viewContext = routerStore.locationType;
			const paneMap = {};

			// see if the "Food" BO is enabled.  If not, we'll have to skip all the ProductHierarchy views
			const foodEnabled = this._isFoodEnabled();
			const formulatedMaterialEnabled = this._isFormulatedMaterialEnabled();

			_.each(this.defView.m_defs, function(viewRef) {
				var id = viewRef.m_id;
				var type = viewRef.rcTarget && viewRef.rcTarget.getType();

				if (!type || (!foodEnabled && id.includes("Food")) || (!formulatedMaterialEnabled && id.includes("FormulatedMaterial")) || !def.getClassViewDef(viewRef.rcTarget, id, true, true)) {
					return;
				}

				var bType = !targetTypes || _.contains(targetTypes, type);
				var bId = !targetIds || _.contains(targetIds, id);

				if (bType || bId) {
					var viewKey = def.composeViewKey(id, type, viewContext);
					// List of hidden views will contain keys with StructureItem, not Season, Category1, Category2, Collection, so check alternate key as well
					const homeViewKey = _.contains(STRUCTURE_ITEM_CLASSES, type) ? def.composeViewKey(id, STRUCTURE_ITEM, viewContext) : null;

					if (!configPreferences.isViewHidden(viewKey) && !configPreferences.isViewHidden(homeViewKey)) {
						csi.objectArrayAddValue(paneMap, id, type);
					}
				}
			});

			var data = _.map(paneMap, function(types, id) {
				return {id: id, name: locale.typeView(types[0], id), types: types};
			});

			csi.sort(data, "name");

			return new Memory({data: data});
		},

		/**
		 * Return JSON string describing home page layout.
		 * @returns {string}
		 */
		getLayout: function() {
			var layout = {
				layout: 0,
				panes: csi.transform(this._gridContainer.getChildren(), _getPaneLayout)
			};

			// merge with currently expanded pane, if any
			var expandContainer = this._expandContainer;
			if (expandContainer) {
				var expandPane = csi.transform(expandContainer.getChildren(), _getPaneLayout)[0];
				expandPane.column = this._expandedColumn;

				var i = _.findIndex(layout.panes, function(pane) {
					return pane.column == expandPane.column;
				});

				if (i === -1) {
					layout.panes.push(expandPane);
				} else {
					layout.panes.splice(i + this._expandedPosition, 0, expandPane);
				}
			}

			return layout;
		},

		/**
		 * Return reflection class of results shown in pane with given id.
		 * @param {string} id
		 * @return {ViewRef}
		 */
		getPaneClass: function(id) {
			var vr = _.findWhere(this.defView.m_defs, {m_id: id});
			if (vr) {
				var defView = vr.getReferencedViewDef();
				return defView.getPageClass();
			}

			return null;
		},

		/**
		 * Create pane to add new widgets.
		 */
		_addNewPane: function() {
			var self = this;

			// reset min width for 2 column layout when adding new page as the pane could change the calculations
			this._minimumWidth = null;

			// get store of items that can be added to home page, making sure disabled status of each item is up to date
			this._newStore = this._newStore || _getNewStore(this.defView);
			this._updateNewStore();

			// create grid for pane
			var grid = new (declare([OnDemandGrid, Selection, GridDijitRegistry]))({
				"class": "csi-homepage-new-grid",
				maintainOddEven: false,
				selectionMode: "single",
				showHeader: false,
				store: this._newStore,
				columns: [
					{field: "name"}
				],
				allowSelect: function(row) {
					return !row.data.disabled;
				},
				renderRow: function(object, options) {
					var div = html.create('div', {"class": object.disabled ? " dijitDisabled" : ""});
					html.create('span', {"class": object.css}, html.create('div', null, div));
					html.create('label', {innerText: object.name}, div);
					return div;
				}
			}, html.create("div", null, this.domNode));

			// create pane
			this._newPane = new TitlePane({
				"class": "csi-homepage-new-pane",
				dragRestriction: true,
				title: locale.resource("DashboardNewPaneTitle"),
				toggleable: false,
				content: grid
			});

			this._gridContainer.addChild(this._newPane, this._gridContainer.getShortestColumn());

			// invoke new action when item selected
			grid.on("dgrid-select", function(event) {
				var item = event.rows[0].data;

				if (item.disabled) {
					return;
				}

				// update disabled status now so that it cannot be selected twice (such as on double-click)
				item.disabled = true;
				grid.store.put(item);

				// delay execution of action - if you call clearSelection on grid within dgrid-select event, it seems to cause an infinite loop
				_.delay(function() {
					csiAction.invoke(self, self.domNode, self.getAction(ACTION_NEW), {
						actionValue: item.id,
						onEnd: function() {
							self._updateNewStore();
						}
					});

					grid.clearSelection();
				});
			});

			grid.resize();
		},

		/**
		 * Create grid container with given number of columns
		 * @param {number} columns
		 */
		_buildGridContainer: function(columns) {
			var self = this;

			return this._gridContainer = new GridContainerLite({
				nbZones: columns,
				isAutoOrganized: false,
				scrollNode: html.pageContentScroll(),
				onMoveChild: function(child) {
					// when a child is moved, update homePageColumn as well, indicating its desired column;
					// the column property could be different if forced to display only 1 column due to limited space
					child.homePageColumn = child.column;
					self._onChangeLayout();
				}
			}).placeAt(this.gridContainerNode);
		},

		/**
		 * Insert view into GridContainer within title pane.
		 * @param {View} defView
		 * @param {Node} [node]
		 * @param {LayoutPane} pane
		 * @param {boolean} waitToOpen
		 * @return {Promise<HomePagePane>}
		 */
		async _buildPane(defView, node, pane, waitToOpen = false) {
			if (!defView || !this.isValidView()) {
				return null;
			}

			const rc = defView.getPageClass();
			if (rc.disabled) {
				return null;
			}

			var grid = this._gridContainer;
			var maxColumn = grid.nbZones - 1;
			var bConfigured = !VIEW_CLASSES[pane.view];
			var viewParams = {
				bDashboard: true,
				plugins: []
			};
			let delayedOpen = waitToOpen && pane.open;

			if (defView.getType() == "Table") {
				// switch controller class and force view to be editable
				defView.bReadOnly = false;
				defView.classController = HomePageTable;

				// ensure that root url is set when context is necessary - node may not exist yet, so let view query for it on its own
				viewParams.urlRoot = pane.contextUrl || this.getViewURL();

				// set state filter enum value
				viewParams.dashboardState = pane.contextState;

				// set dashboard type and name values
				if (bConfigured) {
					if (HomePageUtil.CONTEXT_TYPE_NAMES.includes(pane.contextType)) {
						viewParams.dashboardName = pane.contextName;
					}
					viewParams.dashboardType = pane.contextType;
				}

				// make sure that home page views that reference same view configuration share pool of custom views, but not the selected default
				viewParams.plugins = ['charts', 'filter', 'tableExcel'];
				viewParams.prefNodeContext = pane.id;

				viewParams.getCustomViewNodeContextsStore = function() {
					return new Memory({
						idProperty: "id",
						labelProperty: "name",
						data: [{id: pane.id, name: ""}]
					});
				};

				// register functions to call when layout or table width is changed within view
				viewParams.onActionChangeHomePageLayout = lang.hitch(this, this._onActionLayout);
				viewParams.onActionChangeWidth = lang.hitch(this, this.resize);
			}

			const view = await this._insertView(defView, node, viewParams);
			if (!view) {
				return null;
			}

			const titlePane = new HomePagePane({
				"class": "csi-homepage-pane-" + defView.getHtmlKey(),
				canCopy: bConfigured,
				canDelete: _isEnabledDelete(defView),
				content: view,
				homePageColumn: pane.column,
				homePageId: pane.id,
				contextName: pane.contextName,
				open: !waitToOpen && pane.open,
				duration: 200,
				title: pane.title || defView.getName(),

				onActionChangeTitle: () => {
					this._onChangeLayout();
				},
				onActionCopy: () => {
					csiAction.invoke(this, this.domNode, this.getAction(ACTION_COPY), {actionWidget: titlePane});
				},
				onActionDelete: () => {
					titlePane.destroyRecursive();
					this._onChangeLayout();
				},
				onActionExpand: () => {
					this._toggleExpand(titlePane);
				},
				onActionRefresh: () => {
					csiAction.invoke(view, view.domNode, this.getAction(ACTION_REFRESH));
				}
			});

			titlePane.watch("open", (attr, oldValue, newValue) => {
				const view = titlePane.getView();

				if (!view.isStarted()) {
					when(view.startup(), () => {
						// if all panes are in one column, and all panes are collapsed, then expanding a pane
						// when the browser width is small could require the layout to switch to one column
						if (this.isValidView()) {
							this._onResize();
						}
					});
				}

				// do not persist new layout if opening pane was delayed when view first built
				if (delayedOpen) {
					delayedOpen = false;
				} else {
					this._onChangeLayout();
				}
			});

			// add class to drag node so that it becomes the drag handle for moving the pane
			titlePane.dragNode.classList.add(grid.dragHandleClass);

			grid.addChild(titlePane, Math.min(pane.column, maxColumn), pane.columnPosition);

			if (titlePane.open) {
				await view.startup();
			}

			return titlePane;
		},

		/**
		 * Insert titlepane into home page for given view reference.
		 * @param {ViewRef} viewRef - view to build
		 * @param {LayoutPane} paneParams - parameters to add to pane definition - must include at least target column
		 * @param {boolean} [waitToOpen] - if specified, don't open pane yet
		 * @return {Promise<TitlePane>}
		 */
		async _buildPaneFromViewRef(viewRef, paneParams, waitToOpen = false) {
			// if viewRef requires context, then rootNode may not exist yet (let view get node when needed)
			const rootNode = viewRef.rcTarget ? null : viewRef.getReferencedNode(this.cacheView, this.getViewNode());
			const defView = viewRef.getReferencedViewDef(rootNode);
			const pane = defView ? {
				id: csi.getUniqueId(),
				open: true,
				view: defView.getId(),
				...paneParams
			} : null;

			if (rootNode) {
				paneParams.contextType = rootNode.$Type;
				paneParams.contextUrl = rootNode.$URL;
			}

			return this._buildPane(defView, rootNode, pane, waitToOpen);
		},

		/**
		 * Construct GridContainerLite based on UserPreferences.HomePageLayout.
		 */
		async _buildView() {
			var cache = this.cacheView;
			var defs = this.defView.m_defs;
			var layoutDefinition = _getLayoutDefinition();
			var columns = this._getTargetColumns(layoutDefinition);
			let promises = [];

			this._buildGridContainer(columns);

			if (layoutDefinition) {
				// add views from saved layout
				layoutDefinition.panes?.forEach((pane) => {
					const defView = def.getClassViewDef(reflection.getClass(pane.contextType), pane.view, true);
					const node = cache.getNode(pane.contextUrl);
					const promise = this._buildPane(defView, node, pane, true);

					if (pane.open) {
						promises.push(promise);
					}
				});
			} else {
				// by default, only add fixed views that do not require context
				const fixedViewRefs = _.filter(defs, (viewRef) => !viewRef.rcTarget);

				// split into two columns at position n
				const n = Math.floor((fixedViewRefs.length + 1) / 2);

				// add default views to home page
				promises = fixedViewRefs.map((viewRef, i) => this._buildPaneFromViewRef(viewRef, {column: (i >= n) ? 1 : 0}, true));
			}

			// build panes
			const panesToOpen = await Promise.all(promises);

			// don't let loading of all panes stop views from being used until all are loaded
			this._postView();

			// open panes that should be open
			panesToOpen.forEach((titlePane) => {
				titlePane?.set("open", true);
			});

			if (this._bConfigure) {
				this._addNewPane();
			}
		},

		_isFoodEnabled: function() {
			return reflection.getClass("Food")?.enabled;
		},

		_isFormulatedMaterialEnabled: function() {
			return reflection.getClass("FormulatedMaterial")?.enabled;
		},

		/**
		 * Return number of columns to use on homepage; if browser is not wide enough to support 2 columns, then only show 1.
		 * @param {LayoutDefinition} [layoutDefinition]
		 * @return {number}
		 */
		_getTargetColumns: function(layoutDefinition) {
			var width = this.domNode.offsetWidth;
			var layout = layoutDefinition ? layoutDefinition.layout : 0;
			var desiredColumns = LAYOUTS[layout].columns;
			if (desiredColumns > 1) {
				if (this._minimumWidth) {
					// if we already know the width at which we need to switch to 1 column, then return
					// the desired number of columns if the current width is greater than the minimum width;
					// otherwise keep it at 1
					if (width > this._minimumWidth) {
						this._minimumWidth = null;
						return desiredColumns;
					}

					return 1;
				}

				// determine if any view toolbars overflow, and if so, then switch to 1 column and track width necessary to switch back
				var maxOverflow = 0;

				this.traverseViews(function(view) {
					var toolbarNode = view.toolbarNode && view.toolbarNode.domNode;
					if (toolbarNode) {
						var viewWidth = view.domNode.offsetWidth;
						if (viewWidth) {
							// if the pane is collapsed, then the view width may be 0, so don't involve them in the calculations
							var toolbarWidth = Math.max(toolbarNode.scrollWidth || toolbarNode.offsetWidth, MINIMUM_VIEW_WIDTH);
							var overflow = toolbarWidth - viewWidth;
							maxOverflow = Math.max(maxOverflow, overflow);
						}
					}
				});

				if (maxOverflow) {
					this._minimumWidth = width + (maxOverflow * desiredColumns);
					desiredColumns = 1;
				}
			}

			return desiredColumns;
		},

		/**
		 * Save layout if not previously saved so that any custom view changes use correct context.
		 * @param {Controller} ac
		 */
		_onActionConfigure: function(ac) {
			if (!_getLayoutDefinition()) {
				this._onActionLayout(ac);
			}
		},

		/**
		 * Save current layout.
		 * @param {Controller} ac
		 */
		_onActionLayout: function(ac) {
			_writeLayout(ac.m_writer, this.getLayout());
		},

		/**
		 * Toggle visibility of configure actions for each applicable pane and the new pane section.
		 * @param {boolean} value
		 */
		_onChangeConfigure: function(value) {
			csiAction.invoke(this, this.domNode, this.getAction(ACTION_CONFIGURE));
		},

		/**
		 * Save layout when it is changed, except when in expanded state. The only thing that can trigger this is a
		 * pane being opened as part of the transition to expanded state.
		 */
		_onChangeLayout: function() {
			if (!this._expandContainer) {
				csiAction.invoke(this, this.domNode, this.getAction(ACTION_LAYOUT));
			}
		},

		/**
		 * Toggle between 1 and 2 column layout
		 */
		_onResize: function() {
			var grid = this._gridContainer;
			if (grid) {
				grid.resize();

				var columns = this._getTargetColumns(_getLayoutDefinition());
				if (columns != grid.nbZones) {
					this._setColumns(columns);
				}
			}
		},

		/**
		 * Add necessary paths and URLs from UserPreferences.HomePageLayout
		 * @param {Request} request
		 */
		_onRootRequest: function(request) {
			var layoutDefinition = _getLayoutDefinition();
			if (layoutDefinition) {
				_.each(layoutDefinition.panes, function(pane) {
					if (pane.contextUrl) {
						request.addParameter(comm.QUERY_URL, pane.contextUrl, true);
					}
				});
			}

			this.inherited(arguments);
		},

		/**
		 * Copy given pane, placing the new pane below the given pane.
		 * @param {Controller} ac
		 */
		_preActionCopy: function(ac) {
			var titlePane = ac.actionWidget;
			var pane = _getPaneLayout(titlePane);
			var view = titlePane.getView();
			var defView = def.getClassViewDef(reflection.getClass(pane.contextType), pane.view, true);

			pane.columnPosition = _getPanePosition(titlePane) + 1;
			pane.title = locale.resource('DashboardCopyTitle', [pane.title]);
			pane.id = csi.getUniqueId();

			return this._buildPane(defView, view.getViewNode(), pane).then(function(newTitlePane) {
				ac.newTitlePane = newTitlePane;
				return newTitlePane;
			});
		},

		/**
		 * Add pane for selected productivity item.
		 * @param {LayoutPane} paneParams
		 * @param {ViewRef} viewRef
		 */
		_preActionNew: function(ac, paneParams, viewRef) {
			paneParams = _.extend({
				column: this._newPane.get('column'),
				columnPosition: _getPanePosition(this._newPane),
				contextName: this._newPane.get('contextName')
			}, paneParams);

			return this._buildPaneFromViewRef(viewRef, paneParams).then(function(newTitlePane) {
				ac.newTitlePane = newTitlePane;
				return newTitlePane;
			});
		},

		/**
		 * Toggle visibility of configure actions for each applicable pane and the new pane section.
		 * @param {Controller} ac
		 */
		_postActionConfigure: function(ac) {
			// if expanded, then go back to normal grid view
			if (this._expandContainer) {
				this._toggleExpand(this._expandContainer.getChildren()[0]);
			}

			// control visibility of pane actions
			this._bConfigure = !this._bConfigure;

			domClass.toggle(this.domNode, CSS_CONFIGURE, this._bConfigure);

			if (this._bConfigure) {
				this._addNewPane();
			} else {
				this._newPane.destroyRecursive();
				this._newPane = null;
			}
		},

		/**
		 * Focus on the new pane's title so that it can be renamed easily.
		 * @param {Controller} ac
		 */
		_postActionCopy: function(ac) {
			this.scrollIntoView(ac.newTitlePane.domNode);
			ac.newTitlePane.titleTextBox.focus();
			this._updateNewPane();
		},

		/**
		 * Scroll new pane into view.
		 * @param {Controller} ac
		 */
		_postActionNew: function(ac) {
			this.scrollIntoView(ac.newTitlePane.domNode);
		},

		/**
		 * Reset grid containers before redrawing.
		 * @param {Request} request
		 */
		_redraw: function() {
			if (this._expandContainer) {
				this._expandContainer.destroyRecursive();
				this._expandContainer = null;
				this._expandedColumn = null;
				this._expandedPosition = null;
			}

			if (this._gridContainer) {
				this._gridContainer.destroyRecursive();
				this._gridContainer = null;
				this._newPane = null;
			}

			this.inherited(arguments);
		},

		/**
		 * Add action for layout changes.
		 */
		_resolveView: function() {
			this.defView._addViewAction(ACTION_CONFIGURE, {
				m_onAction: '_onActionConfigure',
				m_postAction: '_postActionConfigure'
			});

			this.defView._addViewAction(ACTION_COPY, {
				m_preAction: '_preActionCopy',
				m_onAction: '_onActionLayout',
				m_postAction: '_postActionCopy'
			});

			this.defView._addViewAction(ACTION_NEW, {
				classController: 'SiteAdmin/HomePage/NewController',
				m_onAction: '_onActionLayout',
				m_postAction: '_postActionNew'
			});

			this.defView._addViewAction(ACTION_LAYOUT, {
				m_onAction: '_onActionLayout',
				m_postAction: '_updateNewPane',
				m_postActionError: 'postActionRedraw'
			});

			this.defView._addViewAction(ACTION_REFRESH);

			this.inherited(arguments);
		},

		/**
		 * Recreate the grid container with the target number of columns
		 * @param {number} columns
		 */
		_setColumns: function(columns) {
			console.assert(this._gridContainer);

			var gc = this._gridContainer;
			var children = gc.getChildren();
			var maxColumn = columns - 1;

			_.each(children, function(child) {
				gc.removeChild(child);
			});

			gc.destroyRecursive();
			gc = this._buildGridContainer(columns);

			_.each(children, function(child) {
				var column = child.homePageColumn;
				if (column == null) {
					column = gc.getShortestColumn();
				}

				gc.addChild(child, Math.min(column, maxColumn));
			});

			this.resize();
		},

		/**
		 * Toggle expand mode containing given pane.
		 * @param {HomePagePane} titlePane
		 */
		_toggleExpand: function(titlePane) {
			var grid = this._gridContainer;
			var expandGrid = this._expandContainer;

			domClass.toggle(this.domNode, CSS_EXPANDED, !expandGrid);

			if (expandGrid) {
				expandGrid.removeChild(titlePane);
				expandGrid.destroyRecursive();
				this._expandContainer = null;

				grid.addChild(titlePane, this._expandedColumn, this._expandedPosition);
				grid.resize();
			} else {
				expandGrid = this._expandContainer = new GridContainerLite({
					nbZones: 1
				}).placeAt(this.expandContainerNode);

				this._expandedColumn = titlePane.get('column');
				this._expandedPosition = _getPanePosition(titlePane);

				grid.removeChild(titlePane);
				expandGrid.addChild(titlePane);

				if (!titlePane.get("open")) {
					titlePane.toggle();
				}

				expandGrid.resize();
			}
		},

		/**
		 * Update column of new pane widget and disabled status of items in new store.
		 */
		_updateNewPane: function() {
			var grid = this._gridContainer;
			var newPane = this._newPane;
			if (newPane) {
				// update new widget store with status of each productivity item
				this._updateNewStore();

				// update column for new pane so that columns are balanced; do so after a delay to make sure it works
				// when a pane is toggled open or closed
				_.delay(function() {
					var oldColumn = newPane.get('column');
					var newColumn = grid.getShortestColumn(newPane);
					if (oldColumn != newColumn) {
						grid.removeChild(newPane);
						grid.addChild(newPane, newColumn);
					}
				}, 300);
			}
		},

		/**
		 * Update disabled status for each item in new store.
		 */
		_updateNewStore: function() {
			var ids = csi.hashArray(this.getLayout().panes, "view", Boolean, {});
			var store = this._newStore;

			store.query().forEach(function(obj) {
				obj.disabled = !!ids[obj.id];
				store.put(obj);
			}, this);
		}
	});
});
